import React, {useState, useEffect, useCallback} from 'react';
import Avatar from '@cloudscape-design/chat-components/avatar';
import {Box, Button, Header, SpaceBetween, SplitPanel, Textarea} from '@cloudscape-design/components';
import AppLayout from '@cloudscape-design/components/app-layout';
import {useStickToBottom} from 'use-stick-to-bottom';
import ChatPanel from './chat-panel';
import ChatMessage from './chat-message';
import SuggestedMessages from './suggested-messages';
import {ArtifactPanelContent} from './artifact-panel';
import {useCassandraChat} from './use-cassandra-chat';
import {DISCLAIMER_TEXT} from './cassandra-types';
import {Chat, listChats, deleteChat} from './cassandra-api';
import {useAuth, ServiceResource} from '../hooks/use-auth';
import {useCassandraSidePane} from './side-pane-context';
import {useRouter} from '../hooks/use-router';
import './cassandra.css';

export default function CassandraChat() {
    const auth = useAuth();
    const credentials = auth.authInformation?.getEndpoints()?.NA?.[ServiceResource.ExplainabilityView]?.credentials;
    const userAlias = auth.authInformation?.email?.split('@')[0];
    const {activeChatId: sharedChatId, setActiveChatId: setSharedChatId} = useCassandraSidePane();
    const router = useRouter();

    const [chats, setChats] = useState<Chat[]>([]);
    const [chatsLoading, setChatsLoading] = useState(true);
    const [activeChatId, setActiveChatId] = useState<string | null>(sharedChatId);
    const [splitPanelOpen, setSplitPanelOpen] = useState(false);
    const [selectedArtifact, setSelectedArtifact] = useState<{artifactId: string; name: string; mimeType: string} | null>(null);
    const {scrollRef, contentRef, isAtBottom, scrollToBottom} = useStickToBottom();

    const setActiveChatIdShared = useCallback(
        (id: string | null) => {
            setActiveChatId(id);
            setSharedChatId(id);
        },
        [setSharedChatId]
    );

    const {messages, status, sending, input, setInput, handleSend, handleNewChat, setMessages, stop, addToolResult} =
        useCassandraChat({
            credentials,
            email: auth.authInformation?.email,
            activeChatId,
            setActiveChatId: setActiveChatIdShared,
        });

    // Load chat list
    useEffect(() => {
        if (!credentials) return;
        setChatsLoading(true);
        listChats(credentials)
            .then(setChats)
            .catch(() => setChats([]))
            .finally(() => setChatsLoading(false));
    }, [credentials]);

    const refreshChats = useCallback(() => {
        if (!credentials) return;
        listChats(credentials)
            .then(setChats)
            .catch(() => {});
    }, [credentials]);

    // Refresh chat list when streaming finishes
    useEffect(() => {
        if (status === 'ready' && activeChatId) refreshChats();
    }, [status, activeChatId, refreshChats]);

    const handleSelectChat = useCallback(
        (chatId: string) => {
            setActiveChatIdShared(chatId);
        },
        [setActiveChatIdShared]
    );

    const handleDeleteChat = useCallback(
        async (chatId: string) => {
            if (!credentials) return;
            await deleteChat(chatId, credentials);
            if (activeChatId === chatId) {
                setActiveChatIdShared(null);
                setMessages([]);
            }
            refreshChats();
        },
        [credentials, activeChatId, refreshChats, setMessages, setActiveChatIdShared]
    );

    const handleSendAndRefresh = useCallback(
        async (overrideText?: string) => {
            const chatId = await handleSend(overrideText);
            if (chatId && chatId !== activeChatId) refreshChats();
        },
        [handleSend, activeChatId, refreshChats]
    );

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendAndRefresh();
        }
    };

    const handleArtifactClick = useCallback((artifact: {artifactId: string; name: string; mimeType: string}) => {
        setSelectedArtifact(artifact);
        setSplitPanelOpen(true);
    }, []);

    if (!credentials) {
        return (
            <Box textAlign="center" padding={{top: 'xxxl'}}>
                <Box variant="h3">Loading Cassandra...</Box>
            </Box>
        );
    }

    return (
        <AppLayout
            navigationWidth={260}
            navigation={
                <ChatPanel
                    chats={chats}
                    activeChatId={activeChatId}
                    loading={chatsLoading}
                    onSelectChat={handleSelectChat}
                    onNewChat={handleNewChat}
                    onDeleteChat={handleDeleteChat}
                />
            }
            splitPanel={
                splitPanelOpen ? (
                    <SplitPanel header={selectedArtifact?.name ?? 'Artifacts'} closeBehavior="hide">
                        <Box padding="s">
                            {selectedArtifact && activeChatId && credentials ? (
                                <ArtifactPanelContent
                                    artifactId={selectedArtifact.artifactId}
                                    mimeType={selectedArtifact.mimeType}
                                    name={selectedArtifact.name}
                                    chatId={activeChatId}
                                    credentials={credentials}
                                />
                            ) : (
                                <Box color="text-body-secondary">Tool outputs and artifacts will appear here.</Box>
                            )}
                        </Box>
                    </SplitPanel>
                ) : undefined
            }
            splitPanelOpen={splitPanelOpen}
            onSplitPanelToggle={({detail}) => setSplitPanelOpen(detail.open)}
            splitPanelPreferences={{position: 'side'}}
            toolsHide
            content={
                <div className="cassandra-main">
                    <Header variant="h2" description="S&OP Forecasting Assistant">
                        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                            <div className="cassandra-tooltip cassandra-tooltip-right" data-tooltip="Exit full screen">
                                <Button
                                    variant="icon"
                                    iconName="shrink"
                                    ariaLabel="Exit full screen"
                                    onClick={() => (window.history.state?.idx > 0 ? router.navigate(-1) : router.navigate('/'))}
                                />
                            </div>
                            Cassandra
                        </SpaceBetween>
                    </Header>
                    <div className="cassandra-messages" ref={scrollRef} role="log">
                        {messages.length === 0 && !sending && (
                            <div className="cassandra-empty-state">
                                <Avatar color="gen-ai" iconName="gen-ai" ariaLabel="Cassandra" width={48} />
                                <Box variant="h3">How can I help you today?</Box>
                                <Box color="text-body-secondary" textAlign="center">
                                    Ask about demand forecasts, S&OP data, or site volumes.
                                </Box>
                            </div>
                        )}
                        <div ref={contentRef} className="max-w-[900px] mx-auto px-4">
                            <SpaceBetween size="xs">
                                {messages.map((msg, i) => (
                                    <ChatMessage
                                        key={msg.id}
                                        message={msg}
                                        userAlias={userAlias}
                                        isStreaming={sending && msg.role === 'assistant' && i === messages.length - 1}
                                        renderArtifactsInline
                                        chatId={activeChatId ?? undefined}
                                        credentials={credentials}
                                        onArtifactClick={handleArtifactClick}
                                        onQuestionAnswer={({toolCallId, answer}) =>
                                            addToolResult({tool: 'askQuestion', toolCallId, output: answer})
                                        }
                                    />
                                ))}
                            </SpaceBetween>
                            {(status === 'ready' || status === 'error') && (
                                <SuggestedMessages messages={messages} onSelect={(text) => handleSendAndRefresh(text)} />
                            )}
                        </div>
                        {!isAtBottom && (
                            <div style={{position: 'sticky', bottom: 32, float: 'right', marginRight: '5%'}}>
                                <Button
                                    variant="icon"
                                    iconName="angle-down"
                                    onClick={() => scrollToBottom()}
                                    ariaLabel="Scroll to bottom"
                                />
                            </div>
                        )}
                    </div>
                    <div className="cassandra-input-area max-w-[900px] mx-auto w-full px-4">
                        <div className="cassandra-prompt-container" onKeyDown={handleKeyDown}>
                            <Textarea
                                value={input}
                                onChange={({detail}) => setInput(detail.value)}
                                placeholder="Type your message here... (Enter to send, Shift+Enter for new line)"
                                disabled={sending}
                                rows={2}
                            />
                            <div className="cassandra-prompt-actions">
                                <Button
                                    variant="icon"
                                    iconName="view-vertical"
                                    ariaLabel="Toggle artifacts panel"
                                    onClick={() => setSplitPanelOpen(!splitPanelOpen)}
                                />
                                {sending ? (
                                    <Button variant="icon" iconName="close" ariaLabel="Stop" onClick={() => stop?.()} />
                                ) : (
                                    <Button
                                        variant="primary"
                                        iconName="send"
                                        ariaLabel="Send message"
                                        onClick={() => handleSendAndRefresh()}
                                        disabled={!input.trim()}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="cassandra-disclaimer">{DISCLAIMER_TEXT}</div>
                    </div>
                </div>
            }
        />
    );
}
